import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { Card, CardBody, Button, Badge, Alert } from '../../components/ui';
import { organizationAPI } from '../../api';

const OrganizationListPage = () => {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrgs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await organizationAPI.list();
      setOrgs(response.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgs();
  }, []);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-[28px] font-sans font-bold text-gray-900 dark:text-white mb-2">
              Organizations
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              Manage all organizations on the platform
            </p>
          </div>
          <Button onClick={() => navigate('/organizations/create')}>
            Create Organization
          </Button>
        </div>

        {error && (
          <Alert variant="error" title="Error" className="mb-6">
            {error}
          </Alert>
        )}

        {loading && !orgs.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardBody>
                  <div className="animate-pulse">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                      <div className="flex-1">
                        <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
                        <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
                      </div>
                    </div>
                    <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        ) : orgs.length === 0 ? (
          <Card>
            <CardBody>
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                No organizations yet. Create one to get started.
              </p>
            </CardBody>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {orgs.map((org) => (
              <Card
                key={org.id}
                hover
                className="cursor-pointer"
                onClick={() => navigate(`/organizations/${org.id}`)}
              >
                <CardBody>
                  <div className="flex items-center gap-4 mb-4">
                    {org.logo_url ? (
                      <img
                        src={org.logo_url}
                        alt={org.name}
                        className="w-12 h-12 rounded-lg object-contain bg-white p-1 border border-gray-100 dark:border-gray-700"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                        {org.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                        {org.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{org.slug}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Badge variant={org.is_active ? 'success' : 'default'}>
                      {org.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    {org.member_count != null && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {org.member_count} member{org.member_count !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default OrganizationListPage;
